import type { ErrorEvent, EventHint } from '@sentry/react';

// Session-expiry statuses handled by the ApolloSetup redirect links (401 per LUM-2005, legacy 403).
// Keep in sync with SESSION_EXPIRY_STATUS_CODES in ApolloSetup.tsx.
const HANDLED_SESSION_EXPIRY_STATUS_CODES = [401, 403];

const handledMarker = (statusCode: number): string => `Received status code ${statusCode}`;

// Drops handled Apollo session-expiry (401/403) ServerErrors: ApolloSetup already redirects and
// captures a warning, but the rejected promise still auto-reports a duplicate (AU-HORIZON-PROD-4YR).
export const sentryBeforeSend = (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
  if (isHandledSessionExpiry(event, hint)) return null;

  const tenant = (event.tags?.tenant as string) || 'default';
  if (event.exception) {
    event.exception.values = event.exception.values?.map((exception: { value?: string }) => ({
      ...exception,
      // Only prefix when a value is present — interpolating an undefined
      // value would produce the literal string "[tenant] undefined".
      value: exception.value ? `[${tenant}] ${exception.value}` : exception.value,
    }));
  }
  return event;
};

const isHandledSessionExpiry = (event: ErrorEvent, hint?: EventHint): boolean => {
  const original = hint?.originalException as { statusCode?: number; message?: string } | undefined;
  const statusCode = original?.statusCode;
  // Require BOTH the statusCode AND its Apollo ServerError marker so we only suppress
  // the canonical handled shape, not any error that happens to carry the same statusCode.
  if (statusCode === undefined || !HANDLED_SESSION_EXPIRY_STATUS_CODES.includes(statusCode)) return false;

  const marker = handledMarker(statusCode);
  if (typeof original?.message === 'string' && original.message.includes(marker)) return true;

  // Wrapped/re-thrown variants: the marker may only appear in the serialised exception
  // chain; the statusCode guard above keeps this fallback narrow.
  const values = event.exception?.values;
  if (values) {
    for (const value of values) {
      if (typeof value?.value === 'string' && value.value.includes(marker)) return true;
    }
  }
  return false;
};
